/**
 * Track measuring: cumulative distance, positions along a track, slices,
 * km markers, direction arrows and nearest points.
 *
 * Distances along a track are haversine metres. Nearest-point searches run
 * in a local equirectangular projection around the track's centre, which is
 * accurate to well under 1 % for course-sized areas.
 */
const EARTH_RADIUS_M = 6371008.8;
const DEG = Math.PI / 180;
export function haversineM(a, b) {
    const dLat = (b[1] - a[1]) * DEG;
    const dLng = (b[0] - a[0]) * DEG;
    const s = Math.sin(dLat / 2) ** 2 +
        Math.cos(a[1] * DEG) * Math.cos(b[1] * DEG) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}
export function localProjection(origin) {
    const mPerDegLat = EARTH_RADIUS_M * DEG;
    const mPerDegLng = mPerDegLat * Math.cos(origin[1] * DEG);
    return {
        origin,
        toXY: (p) => [
            (p[0] - origin[0]) * mPerDegLng,
            (p[1] - origin[1]) * mPerDegLat,
        ],
        toLngLat: (xy) => [
            origin[0] + xy[0] / mPerDegLng,
            origin[1] + xy[1] / mPerDegLat,
        ],
    };
}
/** Compass bearing in degrees (0 = north, clockwise) of a local vector. */
export function bearingOf(dx, dy) {
    return wrap360(Math.atan2(dx, dy) / DEG);
}
export function wrap360(deg) {
    return ((deg % 360) + 360) % 360;
}
export function bboxOf(coords) {
    if (!coords.length)
        return null;
    const box = [Infinity, Infinity, -Infinity, -Infinity];
    for (const c of coords) {
        box[0] = Math.min(box[0], c[0]);
        box[1] = Math.min(box[1], c[1]);
        box[2] = Math.max(box[2], c[0]);
        box[3] = Math.max(box[3], c[1]);
    }
    return box;
}
export function mergeBBoxes(boxes) {
    const valid = boxes.filter((b) => !!b);
    if (!valid.length)
        return null;
    return [
        Math.min(...valid.map((b) => b[0])),
        Math.min(...valid.map((b) => b[1])),
        Math.max(...valid.map((b) => b[2])),
        Math.max(...valid.map((b) => b[3])),
    ];
}
/** Drop consecutive duplicate points. */
function dedupe(coords) {
    const out = [];
    for (const c of coords) {
        const last = out[out.length - 1];
        if (!last || last[0] !== c[0] || last[1] !== c[1])
            out.push(c);
    }
    return out;
}
export function measureTrack(coords, { reversed = false } = {}) {
    const ordered = dedupe(reversed ? [...coords].reverse() : coords);
    const box = bboxOf(ordered) ?? [0, 0, 0, 0];
    const projection = localProjection([
        (box[0] + box[2]) / 2,
        (box[1] + box[3]) / 2,
    ]);
    const xy = ordered.map((c) => projection.toXY(c));
    const cum = [0];
    for (let i = 1; i < ordered.length; i++) {
        cum.push(cum[i - 1] + haversineM(ordered[i - 1], ordered[i]));
    }
    return {
        coords: ordered,
        projection,
        xy,
        cum,
        length: cum[cum.length - 1] ?? 0,
    };
}
/** Index of the segment containing distance m (binary search). */
function segmentAt(track, m) {
    let lo = 0;
    let hi = track.cum.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (track.cum[mid] <= m)
            lo = mid;
        else
            hi = mid;
    }
    return lo;
}
export function positionAt(track, m) {
    const n = track.coords.length;
    if (n === 0) {
        return { m: 0, lngLat: [0, 0], xy: [0, 0], bearing: 0, ele: null };
    }
    if (n === 1) {
        const c = track.coords[0];
        return {
            m: 0,
            lngLat: [c[0], c[1]],
            xy: track.xy[0],
            bearing: 0,
            ele: c[2] ?? null,
        };
    }
    const d = Math.max(0, Math.min(track.length, m));
    const i = Math.min(segmentAt(track, d), n - 2);
    const a = track.coords[i];
    const b = track.coords[i + 1];
    const span = track.cum[i + 1] - track.cum[i];
    const t = span > 0 ? (d - track.cum[i]) / span : 0;
    const lngLat = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    const ax = track.xy[i];
    const bx = track.xy[i + 1];
    const ele = a[2] != null && b[2] != null
        ? a[2] + (b[2] - a[2]) * t
        : (a[2] ?? b[2] ?? null);
    return {
        m: d,
        lngLat,
        xy: [ax[0] + (bx[0] - ax[0]) * t, ax[1] + (bx[1] - ax[1]) * t],
        bearing: bearingOf(bx[0] - ax[0], bx[1] - ax[1]),
        ele,
    };
}
/** The track between two distances, as [lng, lat] points. */
export function sliceTrack(track, fromM, toM) {
    const from = Math.max(0, Math.min(fromM, toM));
    const to = Math.min(track.length, Math.max(fromM, toM));
    const points = [positionAt(track, from).lngLat];
    for (let i = 0; i < track.coords.length; i++) {
        if (track.cum[i] > from && track.cum[i] < to) {
            points.push([track.coords[i][0], track.coords[i][1]]);
        }
    }
    points.push(positionAt(track, to).lngLat);
    return fromM <= toM ? points : points.reverse();
}
function projectOnSegment(p, a, b) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0
        ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2))
        : 0;
    const x = a[0] + dx * t - p[0];
    const y = a[1] + dy * t - p[1];
    return { t, d2: x * x + y * y };
}
/** Per-segment distance from a point, for pass detection. */
export function segmentDistances(track, lngLat) {
    const p = track.projection.toXY(lngLat);
    const out = [];
    for (let i = 0; i < track.xy.length - 1; i++) {
        const { t, d2 } = projectOnSegment(p, track.xy[i], track.xy[i + 1]);
        out.push({
            m: track.cum[i] + (track.cum[i + 1] - track.cum[i]) * t,
            d: Math.sqrt(d2),
        });
    }
    return out;
}
export function nearestOnTrack(track, lngLat) {
    if (track.coords.length < 2)
        return null;
    const distances = segmentDistances(track, lngLat);
    let best = 0;
    for (let i = 1; i < distances.length; i++) {
        if (distances[i].d < distances[best].d)
            best = i;
    }
    const { m, d } = distances[best];
    return {
        m,
        distanceM: d,
        lngLat: positionAt(track, m).lngLat,
        segment: best,
    };
}
/** Whole km along the track, excluding start and finish. */
export function kmMarkers(track) {
    const out = [];
    for (let km = 1; km * 1000 < track.length - 50; km++) {
        out.push({ km, lngLat: positionAt(track, km * 1000).lngLat });
    }
    return out;
}
/** Direction arrows every km, at km n + 0.7, not too close to the finish. */
export function directionArrows(track) {
    const out = [];
    for (let m = 700; m < track.length - 300; m += 1000) {
        const p = positionAt(track, m);
        out.push({ m, lngLat: p.lngLat, bearing: p.bearing });
    }
    return out;
}
/** Length of a polyline in metres. */
export function lineLengthM(coords) {
    let total = 0;
    for (let i = 1; i < coords.length; i++) {
        total += haversineM(coords[i - 1], coords[i]);
    }
    return total;
}
/** Point halfway along a polyline. */
export function lineMidpoint(coords) {
    if (coords.length === 1)
        return coords[0];
    const half = lineLengthM(coords) / 2;
    let walked = 0;
    for (let i = 1; i < coords.length; i++) {
        const seg = haversineM(coords[i - 1], coords[i]);
        if (walked + seg >= half) {
            const t = seg > 0 ? (half - walked) / seg : 0;
            return [
                coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * t,
                coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * t,
            ];
        }
        walked += seg;
    }
    return coords[coords.length - 1];
}
/**
 * Douglas–Peucker simplification in local metres. Keeps elevation values of
 * the points it keeps.
 */
export function simplifyLine(coords, toleranceM = 2) {
    if (coords.length <= 2)
        return coords;
    const box = bboxOf(coords);
    const projection = localProjection([
        (box[0] + box[2]) / 2,
        (box[1] + box[3]) / 2,
    ]);
    const xy = coords.map((c) => projection.toXY(c));
    const keep = new Uint8Array(coords.length);
    keep[0] = 1;
    keep[coords.length - 1] = 1;
    const tol2 = toleranceM * toleranceM;
    const stack = [[0, coords.length - 1]];
    while (stack.length) {
        const [first, last] = stack.pop();
        let maxD2 = 0;
        let index = -1;
        for (let i = first + 1; i < last; i++) {
            const { d2 } = projectOnSegment(xy[i], xy[first], xy[last]);
            if (d2 > maxD2) {
                maxD2 = d2;
                index = i;
            }
        }
        if (index !== -1 && maxD2 > tol2) {
            keep[index] = 1;
            stack.push([first, index], [index, last]);
        }
    }
    return coords.filter((_, i) => keep[i]);
}
